/**
 * Money denomination style definitions and display helpers.
 *
 * SINGLE SOURCE OF TRUTH for all money rendering (MoneyVisual + MoneyAssembler).
 * Extracted from MoneyAssembler.tsx during CL-1 s65, unified with MoneyVisual in s-money-refactor.
 *
 * Pure data + pure functions — no React dependency.
 */

import type { MoneyDenom } from '../../content/types'

// ── Style Types ──────────────────────────────────────────────

export interface DenomStyle {
  container: string
  bg: string
  text: string
  border: string
  inner: string
}

// ── Bill Styles ──────────────────────────────────────────────

const BILL_STYLES: Record<number, DenomStyle> = {
  50000: {
    container: 'w-28 h-16 md:w-32 md:h-20 rounded-xl shadow-xl',
    bg: 'bg-orange-500',
    text: 'text-white font-black text-2xl md:text-3xl drop-shadow-lg',
    border: 'border-4 border-orange-700/40',
    inner: 'bg-white/15 backdrop-blur-sm',
  },
  20000: {
    container: 'w-24 h-14 md:w-28 md:h-16 rounded-xl shadow-xl',
    bg: 'bg-blue-500',
    text: 'text-white font-black text-2xl md:text-3xl drop-shadow-lg',
    border: 'border-4 border-blue-700/40',
    inner: 'bg-white/15 backdrop-blur-sm',
  },
  10000: {
    container: 'w-24 h-14 md:w-28 md:h-16 rounded-xl shadow-xl',
    bg: 'bg-rose-500',
    text: 'text-white font-black text-xl md:text-2xl drop-shadow-lg',
    border: 'border-4 border-rose-700/40',
    inner: 'bg-white/15 backdrop-blur-sm',
  },
  5000: {
    container: 'w-28 h-16 md:w-32 md:h-20 rounded-xl shadow-xl',
    bg: 'bg-orange-500',
    text: 'text-white font-black text-2xl md:text-3xl drop-shadow-lg',
    border: 'border-4 border-orange-700/40',
    inner: 'bg-white/15 backdrop-blur-sm',
  },
  2000: {
    container: 'w-24 h-14 md:w-28 md:h-16 rounded-xl shadow-xl',
    bg: 'bg-blue-500',
    text: 'text-white font-black text-2xl md:text-3xl drop-shadow-lg',
    border: 'border-4 border-blue-700/40',
    inner: 'bg-white/15 backdrop-blur-sm',
  },
  1000: {
    container: 'w-24 h-14 md:w-28 md:h-16 rounded-xl shadow-xl',
    bg: 'bg-green-500',
    text: 'text-white font-black text-xl md:text-2xl drop-shadow-lg',
    border: 'border-4 border-green-700/40',
    inner: 'bg-white/15 backdrop-blur-sm',
  },
  500: {
    container: 'w-20 h-12 md:w-24 md:h-14 rounded-xl shadow-lg',
    bg: 'bg-teal-500',
    text: 'text-white font-black text-xl md:text-2xl drop-shadow-lg',
    border: 'border-2 border-teal-700/40',
    inner: 'bg-white/15 backdrop-blur-sm',
  },
}

// ── Coin Styles ──────────────────────────────────────────────

const COIN_STYLES: Record<number, DenomStyle> = {
  200: {
    container: 'w-16 h-16 md:w-18 md:h-18 rounded-full shadow-2xl',
    bg: 'bg-slate-200',
    text: 'text-slate-800 font-black text-lg md:text-xl',
    border: 'border-[3px] border-slate-500',
    inner:
      'bg-amber-400 rounded-full w-10 h-10 md:w-12 md:h-12 flex items-center justify-center shadow-inner border-2 border-amber-600/40',
  },
  100: {
    container: 'w-14 h-14 md:w-16 md:h-16 rounded-full shadow-2xl',
    bg: 'bg-amber-400',
    text: 'text-amber-900 font-black text-lg md:text-xl',
    border: 'border-[3px] border-amber-600',
    inner:
      'bg-slate-200 rounded-full w-9 h-9 md:w-11 md:h-11 flex items-center justify-center shadow-inner border-2 border-slate-500/40',
  },
  50: {
    container: 'w-14 h-14 md:w-16 md:h-16 rounded-full shadow-xl',
    bg: 'bg-yellow-400',
    text: 'text-yellow-900 font-black text-base md:text-lg',
    border: 'border-[3px] border-yellow-600',
    inner: 'w-full h-full flex items-center justify-center',
  },
  20: {
    container: 'w-12 h-12 md:w-14 md:h-14 rounded-full shadow-lg',
    bg: 'bg-yellow-400',
    text: 'text-yellow-900 font-black text-base md:text-lg',
    border: 'border-[3px] border-yellow-600',
    inner: 'w-full h-full flex items-center justify-center',
  },
  10: {
    container: 'w-11 h-11 md:w-13 md:h-13 rounded-full shadow-lg',
    bg: 'bg-yellow-400',
    text: 'text-yellow-900 font-black text-sm md:text-base',
    border: 'border-[3px] border-yellow-700',
    inner: 'w-full h-full flex items-center justify-center',
  },
  5: {
    container: 'w-10 h-10 md:w-12 md:h-12 rounded-full shadow-md',
    bg: 'bg-orange-400',
    text: 'text-orange-900 font-black text-sm md:text-base',
    border: 'border-[3px] border-orange-700',
    inner: 'w-full h-full flex items-center justify-center',
  },
  2: {
    container: 'w-9 h-9 md:w-11 md:h-11 rounded-full shadow-md',
    bg: 'bg-orange-400',
    text: 'text-orange-900 font-black text-xs md:text-sm',
    border: 'border-[2.5px] border-orange-700',
    inner: 'w-full h-full flex items-center justify-center',
  },
  1: {
    container: 'w-8 h-8 md:w-10 md:h-10 rounded-full shadow-md',
    bg: 'bg-orange-400',
    text: 'text-orange-900 font-black text-xs md:text-sm',
    border: 'border-[2.5px] border-orange-700',
    inner: 'w-full h-full flex items-center justify-center',
  },
}

const DEFAULT_STYLE: DenomStyle = {
  container: 'w-12 h-12 rounded-full',
  bg: 'bg-gray-200',
  text: 'text-gray-800 font-bold',
  border: 'border border-gray-300',
  inner: '',
}

// ── Euro denomination constants ──────────────────────────────

/** Euro bill values in cents (descending) */
export const EURO_BILLS_CENTS = [
  50000, 20000, 10000, 5000, 2000, 1000, 500,
] as const

/** Euro coin values in cents (descending) */
export const EURO_COINS_CENTS = [200, 100, 50, 20, 10, 5, 2, 1] as const

// ── Public Helpers ────────────────────────────────────────────

/** Get visual style for a denomination value in cents. */
export function getMoneyStyles(cents: number): DenomStyle {
  return BILL_STYLES[cents] ?? COIN_STYLES[cents] ?? DEFAULT_STYLE
}

/** Check whether a denomination value (in cents) is a bill. */
export function isBill(cents: number, allowedDenoms?: MoneyDenom[]): boolean {
  if (allowedDenoms) {
    const denom = allowedDenoms.find((d) => d.valueCents === cents)
    if (denom) return denom.type === 'bill'
  }
  // Fallback: ≥500 cents (€5) is a bill
  return cents >= 500
}

/** Get display label for a denomination (uses config label or € formatting). */
export function getDenominationDisplay(
  cents: number,
  allowedDenoms?: MoneyDenom[],
): string {
  if (allowedDenoms) {
    const denom = allowedDenoms.find((d) => d.valueCents === cents)
    if (denom?.label) return denom.label
  }
  if (cents >= 100) return `€${cents / 100}`
  return `${cents}c`
}
